import React, { useContext } from 'react';
import { Form, useChanges } from '../Form/Form';
import { TextField } from '../Form/TextField/TextField';
import { Button } from '../Button/Button';
import { t } from '../../locales/translate';
import { loadContingent, Contingent, changeCapacity, loadAllocations } from '../../model/contingent';
import { Event, getEventSalesStatus } from '../../model/event';
import { RadioGroup } from '../Form/Radio/RadioGroup';
import { Label } from '../Form/Label/Label';
import { SubmitLine } from '../SubmitLine/SubmitLine';
import { TextArea } from '../Form/TextArea/TextArea';
import { AnimatedHeight } from '../AnimatedHeight/AnimatedHeight';
import { ActivitiesContext } from '../Activities/Activities';
import { Stack } from '../Stack/Stack';

interface Props {
  section: { id: string };
  event: Event;
  contingent: Contingent;
  onClose: () => void;
}

interface Params {
  capacity: number;
  status: 'available' | 'blocked';
  blockReason: string;
}

export function CapacityEditForm({ section, onClose, event, contingent }: Props): JSX.Element {
  const [changes, setChanges, model] = useChanges<Params>({
    capacity: contingent.capacity,
    status: 'available',
    blockReason: '',
  });
  const { reloadActivities } = useContext(ActivitiesContext);

  const canSubmit = Object.keys(changes).length > 0;

  async function submit(): Promise<void> {
    await changeCapacity({
      eventId: event.id,
      sectionId: section.id,
      amount: model.capacity - contingent.capacity,
      blockReason: model.status === 'blocked' ? model.blockReason : null,
    });
    await loadContingent(event, section.id);
    await loadAllocations(event, { updateGeneralAdmissionOnly: true });
    await getEventSalesStatus(event.id, { force: true });
    reloadActivities();
    onClose();
  }

  return (
    <Form model={model} onInput={setChanges} onSubmit={submit}>
      <AnimatedHeight>
        <Stack>
          <TextField
            name="capacity"
            label={t.model.contingent.capacity}
            type="number"
            min={contingent.capacity - contingent.available}
            required
          />
          {(changes.capacity ?? 0) > contingent.capacity && (
            <>
              <Label title={t.model.contingent.newTickets} />
              <p>{t.model.contingent.newTicketsWillBeCreated}</p>
              <RadioGroup
                name="status"
                values={[
                  { value: 'available', label: t.model.contingent.makeAvailable },
                  { value: 'blocked', label: t.model.contingent.blockThem },
                ]}
              />
              {model.status === 'blocked' && (
                <TextArea name="blockReason" label={t.model.contingent.blockReason} required />
              )}
            </>
          )}
        </Stack>
      </AnimatedHeight>
      <SubmitLine>
        <Button title={t.actions.cancel('')} onClick={onClose} />
        <Button title={t.actions.save('')} type="primary" disabled={!canSubmit} isSubmit />
      </SubmitLine>
    </Form>
  );
}
